// src/services/cadence_api.js
//
// Admin-only client for per-group session cadence (see
// docs/WEEK_INTERVAL_AND_WEEK_START.md, Part D, in the API repo).
//
// Every call here is live. The page was built first against an in-memory
// stand-in so the admin workflow could be reviewed before a schema was
// committed to; that stand-in has served its purpose and is gone.

import { apiClient } from "./api_client";

// Kept as exports so components need no edit if a stand-in is ever reintroduced.
// Validation lives on the server by design: the doc requires the preview and the
// enforced gate to be the same code, or the page can confidently show a
// transition that differs from what actually happens.
export const cadenceIsMocked = false;
export const cadenceWritesAreMocked = false;

// GET /group/list?detail=true — real endpoint, returns full group dicts.
async function listGroups() {
  const { data } = await apiClient.get("/group/list", {
    params: { detail: true },
  });
  // A non-admin gets [] rather than an error, same as the rest of the admin API.
  return Array.isArray(data) ? data : [];
}

const cadenceApi = {
  listGroups,

  // PUT /group/{id}/reminders — real, not mocked. Scoped to outbound messaging:
  // a disabled group is skipped by the dispatcher before any slot is claimed,
  // so nothing queues and re-enabling leaves the day's schedule free to fire.
  setRemindersEnabled: async (groupId, enabled) => {
    const { data } = await apiClient.put(`/group/${groupId}/reminders`, { enabled });
    if (data?.message) throw new Error(data.message);
    return data;
  },

  // GET /group/cadence — one row per group with its current policy and a
  // pending-change marker. Drives View 1.
  listCadence: async () => {
    // Carries the group's identity, participant counts and reminders_enabled
    // as well as its policy, so the page needs no second call to /group/list.
    const { data } = await apiClient.get("/group/cadence");
    return data?.groups ?? [];
  },

  // GET /group/{id}/cadence — the group's full append-only policy history,
  // including superseded rows. Drives View 2.
  history: async (groupId) => {
    const { data } = await apiClient.get(`/group/${groupId}/cadence`);
    return data?.policies ?? [];
  },

  // POST /group/{id}/cadence/preview — pure, read-only. Returns validity,
  // violations, the transition block layout and impact counts. Drives View 3.
  //
  // This must stay server-computed once the backend exists: the preview and
  // the enforced gate have to be the same math, or the page will confidently
  // show a transition that differs from what actually happens.
  preview: async (groupId, body) => {
    const { data } = await apiClient.post(
      `/group/${groupId}/cadence/preview`,
      body
    );
    return data;
  },

  // POST /group/{id}/cadence — appends one policy row. Never updates, never
  // deletes an effective row. The server re-runs the full validation.
  schedule: async (groupId, body) => {
    const { data } = await apiClient.post(`/group/${groupId}/cadence`, body);
    return data;
  },

  // DELETE /group/{id}/cadence/{policyId} — withdraw a row that has not taken
  // effect. Rows whose effective_from has passed are immutable forever.
  withdraw: async (groupId, policyId) => {
    const { data } = await apiClient.delete(
      `/group/${groupId}/cadence/${policyId}`
    );
    return data;
  },
};

export default cadenceApi;
